import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import DatePicker from "components/cck/DatePicker";

import { updateVisitWindowSchema, UpdateVisitWindowSchemaInput, UpdateVisitWindowSchemaOutput } from "view/connectingVisits/utils/connectingvisit.schema";

import { EditConnectingVisitWindowDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";

const UpdateVisitWindowDialog = ({ open, onClose, visit, refetch }: {
	open: boolean,
	onClose: () => void,
	visit: {
		id: number;
		from: number | null,
		to: number | null,
	}
	refetch: () => void,
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateVisitWindowSchemaInput, unknown, UpdateVisitWindowSchemaOutput>({
		resolver: zodResolver(updateVisitWindowSchema),
		defaultValues: {
			from: null,
			to: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			from: null,
			to: null,
		});
	};

	const [ editConnectingVisitWindow, { loading } ] = useMutation(EditConnectingVisitWindowDocument);

	const save = (data: UpdateVisitWindowSchemaOutput) => {
		editConnectingVisitWindow({
			variables: {
				id: visit.id,
				from: data.from,
				to: data.to,
			},
		}).then(() => {
			refetch();
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				from: visit.from,
				to: visit.to,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "START_END_WINDOW" })}
			form='updateVisitWindowForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateVisitWindowForm">
				<FormGroup label={intl.formatMessage({ id: "FROM" })} overflow="hidden">
					<Controller
						name="from"
						control={control}
						render={({ field }) => <DatePicker { ...field } showTimeInput autoFocus />} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "TO" })} overflow="hidden">
					<Controller
						name="to"
						control={control}
						render={({ field }) => <DatePicker { ...field } showTimeInput />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateVisitWindowDialog;
